import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { MarketPdsForm, type MarketPdsFormValues } from './MarketPdsForm';

export const Route = createFileRoute('/admin-console/platform-content/market-pds/add')({
    component: AddMarketPdsRoute,
    staticData: {
        presentation: 'overlay',
    },
});

function AddMarketPdsRoute() {
    const { isReadOnly } = useAdminModulePermissions('comms');

    if (isReadOnly) {
        return <Navigate to="/admin-console/platform-content/market-pds" />;
    }

    return (
        <PageController route={Route}>
            <AddMarketPds />
        </PageController>
    );
}

function AddMarketPds() {
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();

    const { data: categories } = trpc.admin.marketPds.listCategories.useQuery();
    const { data: insurers } = trpc.admin.marketPds.listInsurers.useQuery();

    const { mutate: createMarketPds, isPending: isCreating } = trpc.admin.marketPds.createMarketPds.useMutation({
        onError: (error) => errorMessage.show(error),
    });

    const handleSubmit = (values: MarketPdsFormValues) => {
        const key = 'create_pds';
        message.loading({ content: 'Creating...', key });

        createMarketPds(
            {
                name: values.name ?? '',
                insurerName: values.insurerName ?? '',
                category: values.category ?? '',
                year: values.year ?? new Date().getFullYear(),
                insurerLogo: values.insurerLogo ?? null,
                version: values.version ?? null,
                effectiveDate: values.effectiveDate ?? null,
            },
            {
                onSuccess: () => {
                    message.success({ content: 'PDS created', key });
                    trpcUtils.admin.marketPds.listMarketPds.invalidate();
                    trpcUtils.admin.marketPds.listCategories.invalidate();
                    trpcUtils.admin.marketPds.listInsurers.invalidate();
                    setOpen(false);
                },
                onError: (error) => {
                    message.error(error.message || 'Failed to create PDS');
                },
            },
        );
    };

    return (
        <MarketPdsForm
            open={open}
            mode="add"
            data={undefined}
            categories={categories ?? []}
            insurers={insurers?.map((i) => i.insurerName) ?? []}
            state={match({ isCreating })
                .with({ isCreating: true }, () => 'submitting' as const)
                .otherwise(() => 'ready' as const)}
            onSubmit={handleSubmit}
            onCloseFinished={() => {
                navigate({ to: '/admin-console/platform-content/market-pds' });
            }}
        />
    );
}
